import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import React from 'react';

export interface UseTypingTestOptions {
  text: string;
  duration: number;
  /**
   * Called when the test ends (timer or text complete).
   * Receives the final typed input, the actual elapsed duration in seconds,
   * and the keystroke timestamps array for consistency calculation.
   * May be async — the hook does not await it.
   */
  onEnd: (input: string, actualDuration: number, timestamps: number[]) => void;
}

export interface UseTypingTestReturn {
  userInput: string;
  timeLeft: number;
  testStarted: boolean;
  testEnded: boolean;
  hasInputError: boolean;
  isFocused: boolean;
  setIsFocused: (v: boolean) => void;
  lineTransition: boolean;
  textBlockWidth: number;
  displayWPM: number;
  currentWord: string;
  renderVisibleText: React.ReactNode;
  inputRef: React.RefObject<HTMLInputElement | null>;
  containerRef: React.RefObject<HTMLDivElement | null>;
  handleKeyDown: (e: React.KeyboardEvent<HTMLInputElement>) => void;
  handleInputChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  handleContainerClick: (e: React.MouseEvent) => void;
  formatTime: (s: number) => string;
  /** Resets all typing state. Call before loading new text in practice mode. */
  reset: () => void;
}

export function useTypingTest({ text, duration, onEnd }: UseTypingTestOptions): UseTypingTestReturn {
  const [userInput, setUserInput] = useState('');
  const [timeLeft, setTimeLeft] = useState(duration);
  const [testStarted, setTestStarted] = useState(false);
  const [testEnded, setTestEnded] = useState(false);
  const [startTime, setStartTime] = useState<Date | null>(null);
  const [currentLineIndex, setCurrentLineIndex] = useState(0);
  const [lines, setLines] = useState<string[]>([]);
  const [lineStarts, setLineStarts] = useState<number[]>([0]);
  const [lastCorrectSpaceIndex, setLastCorrectSpaceIndex] = useState(-1);
  const [hasInputError, setHasInputError] = useState(false);
  const [isFocused, setIsFocused] = useState(true);
  const [textBlockWidth, setTextBlockWidth] = useState(0);
  const [lineTransition, setLineTransition] = useState(false);
  const [displayWPM, setDisplayWPM] = useState(0);

  const inputRef = useRef<HTMLInputElement>(null);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const userInputRef = useRef(userInput);
  const textRef = useRef(text);
  const keystrokeTimestampsRef = useRef<number[]>([]);
  const isEndingRef = useRef(false);

  userInputRef.current = userInput;
  textRef.current = text;

  // Calculate line breaks when text changes
  useEffect(() => {
    if (!text || !containerRef.current) return;

    const style = getComputedStyle(containerRef.current);
    const paddingH = parseFloat(style.paddingLeft) + parseFloat(style.paddingRight);
    const contentWidth = containerRef.current.offsetWidth - paddingH;
    const fontSize = parseFloat(style.fontSize);
    const charsPerLine = Math.min(65, Math.max(20, Math.floor(contentWidth / (fontSize * 0.55))));

    const newLines: string[] = [];
    const newLineStarts: number[] = [0];
    let currentLine = '';
    let charCount = 0;

    const words = text.split(' ');
    for (let i = 0; i < words.length; i++) {
      const word = words[i];
      const wordWithSpace = i < words.length - 1 ? word + ' ' : word;
      if (currentLine.length + wordWithSpace.length > charsPerLine && currentLine.length > 0) {
        newLines.push(currentLine);
        charCount += currentLine.length;
        newLineStarts.push(charCount);
        currentLine = wordWithSpace;
      } else {
        currentLine += wordWithSpace;
      }
    }
    if (currentLine) newLines.push(currentLine);

    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    if (ctx && containerRef.current) {
      ctx.font = getComputedStyle(containerRef.current).font;
      let maxWidth = 0;
      for (const line of newLines) {
        const w = ctx.measureText(line).width;
        if (w > maxWidth) maxWidth = w;
      }
      setTextBlockWidth(maxWidth);
    }

    setLines(newLines);
    setLineStarts(newLineStarts);
  }, [text]); // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-focus when text becomes available
  useEffect(() => {
    if (text && inputRef.current) inputRef.current.focus();
  }, [text]);

  // Timer. Time left is worked out from the start time, not by counting ticks: browsers slow or pause
  // intervals in background tabs, and counting ticks would let the clock stop while the candidate is away.
  useEffect(() => {
    if (!testStarted || testEnded || !startTime) return;
    const update = () => {
      const elapsed = (Date.now() - startTime.getTime()) / 1000;
      setTimeLeft(Math.max(0, Math.ceil(duration - elapsed)));
    };
    update();
    timerRef.current = setInterval(update, 250);
    // Catch up immediately when the tab becomes visible again
    document.addEventListener('visibilitychange', update);
    window.addEventListener('focus', update);
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
      document.removeEventListener('visibilitychange', update);
      window.removeEventListener('focus', update);
    };
  }, [testStarted, testEnded, startTime, duration]);

  // True once the full duration has passed, even if the timer hasn't ticked yet
  const timeIsUp = useCallback(
    () => !!startTime && Date.now() - startTime.getTime() >= duration * 1000,
    [startTime, duration]
  );

  // Update current line with slide animation
  useEffect(() => {
    if (lines.length === 0) return;
    let newLineIndex = 0;
    for (let i = 0; i < lineStarts.length; i++) {
      if (userInput.length >= lineStarts[i]) newLineIndex = i;
    }
    if (newLineIndex !== currentLineIndex) {
      if (testStarted) {
        setLineTransition(true);
        setTimeout(() => setLineTransition(false), 120);
      }
      setCurrentLineIndex(newLineIndex);
    }
  }, [userInput.length, lineStarts, lines.length, currentLineIndex, testStarted]);

  // Prevent space from scrolling the page after test ends
  useEffect(() => {
    if (testEnded) {
      const prevent = (e: KeyboardEvent) => { if (e.key === ' ') e.preventDefault(); };
      document.addEventListener('keydown', prevent);
      return () => document.removeEventListener('keydown', prevent);
    }
  }, [testEnded]);

  // Live WPM (updates every second while test is active)
  useEffect(() => {
    if (!testStarted || testEnded) { setDisplayWPM(0); return; }
    const wpmInterval = setInterval(() => {
      const input = userInputRef.current;
      const txt = textRef.current;
      if (!startTime || input.length === 0) return;
      const elapsedMin = (Date.now() - startTime.getTime()) / 60000;
      if (elapsedMin <= 0) return;
      const correctChars = input.split('').filter((c, i) => c === txt[i]).length;
      setDisplayWPM(Math.round((correctChars / 5) / elapsedMin));
    }, 1000);
    return () => clearInterval(wpmInterval);
  }, [testStarted, testEnded, startTime]);

  const startTest = useCallback(() => {
    keystrokeTimestampsRef.current = [];
    setTestStarted(true);
    setStartTime(new Date());
  }, []);

  const endTest = useCallback(() => {
    if (testEnded || isEndingRef.current) return;
    isEndingRef.current = true;
    if (timerRef.current) clearInterval(timerRef.current);

    // Never more than the test length (the page may only notice the time is up after returning to the tab)
    const actualDuration = startTime
      ? Math.min(duration, (new Date().getTime() - startTime.getTime()) / 1000)
      : duration;

    onEnd(userInputRef.current, parseFloat(actualDuration.toFixed(3)), [...keystrokeTimestampsRef.current]);
    setTestEnded(true);
  }, [testEnded, startTime, duration, onEnd]);

  // End test when timer reaches zero
  useEffect(() => {
    if (timeLeft === 0 && testStarted && !testEnded) {
      endTest();
    }
  }, [timeLeft, testStarted, testEnded, endTest]);

  const handleKeyDown = useCallback((e: React.KeyboardEvent<HTMLInputElement>) => {
    if (testEnded) { e.preventDefault(); return; }
    if (timeIsUp()) { e.preventDefault(); endTest(); return; }
    if (e.key === 'Backspace') {
      if (userInput.length <= lastCorrectSpaceIndex + 1) e.preventDefault();
      setHasInputError(false);
      return;
    }
    if (e.key === 'Tab' || e.key === 'Escape') { e.preventDefault(); return; }
    if (!testStarted && e.key.length === 1) startTest();
  }, [testEnded, testStarted, startTest, userInput.length, lastCorrectSpaceIndex, timeIsUp, endTest]);

  const handleInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    if (testEnded) return;
    // Keystrokes after the time is up don't count
    if (timeIsUp()) { e.target.value = userInput; endTest(); return; }
    const newValue = e.target.value;
    const txt = textRef.current;

    if (newValue.length < userInput.length) {
      if (newValue.length > lastCorrectSpaceIndex) {
        setUserInput(newValue);
      } else {
        e.target.value = userInput;
      }
      return;
    }

    if (newValue.length > userInput.length) {
      if (testStarted) keystrokeTimestampsRef.current.push(Date.now());
      const newChar = newValue[newValue.length - 1];
      const charIndex = newValue.length - 1;

      if (newChar === ' ') {
        if (charIndex === 0 || (userInput.length > 0 && userInput[charIndex - 1] === ' ')) {
          e.target.value = userInput;
          return;
        }
        if (txt[charIndex] === ' ') {
          setLastCorrectSpaceIndex(charIndex);
          setUserInput(newValue);
        } else if (charIndex > lastCorrectSpaceIndex + 1) {
          const nextSpaceIdx = txt.indexOf(' ', charIndex);
          if (nextSpaceIdx === -1) { e.target.value = userInput; return; }
          let autoInput = userInput;
          for (let i = charIndex; i < nextSpaceIdx; i++) {
            autoInput += ' ';
          }
          autoInput += ' ';
          setLastCorrectSpaceIndex(nextSpaceIdx);
          setUserInput(autoInput);
          e.target.value = autoInput;
          setHasInputError(true);
          setTimeout(() => setHasInputError(false), 300);
          if (autoInput.length >= txt.length) endTest();
        } else {
          e.target.value = userInput;
          return;
        }
      } else {
        setUserInput(newValue);
        if (newValue[charIndex] !== txt[charIndex]) {
          setHasInputError(true);
          setTimeout(() => setHasInputError(false), 300);
        }
        if (newValue.length >= txt.length) endTest();
      }
    }
  }, [testEnded, userInput, endTest, lastCorrectSpaceIndex, testStarted, timeIsUp]);

  const currentWord = useMemo(() => {
    if (!text || userInput.length === 0) return '';
    let wordStart = userInput.lastIndexOf(' ');
    wordStart = wordStart === -1 ? 0 : wordStart + 1;
    return userInput.slice(wordStart);
  }, [userInput, text]);

  const renderVisibleText = useMemo(() => {
    if (lines.length === 0) return React.createElement('span', { className: 'char' }, text);
    const startLine = currentLineIndex;
    const endLine = Math.min(startLine + 2, lines.length);
    return lines.slice(startLine, endLine).map((line, lineIdx) => {
      const globalLineIndex = startLine + lineIdx;
      const lineStart = lineStarts[globalLineIndex] || 0;
      return React.createElement(
        'div',
        { key: globalLineIndex, className: 'text-line' },
        React.createElement(
          'span',
          null,
          line.split('').map((char, charIdx) => {
            const gi = lineStart + charIdx;
            let cls = 'char';
            if (gi < userInput.length) {
              cls += userInput[gi] === char ? ' correct' : ' incorrect';
            } else if (gi === userInput.length) {
              cls += ' cursor';
            }
            return React.createElement('span', { key: charIdx, className: cls }, char);
          })
        )
      );
    });
  }, [lines, lineStarts, userInput, currentLineIndex, text]);

  const formatTime = (s: number) => {
    const m = Math.floor(s / 60);
    return `${m}:${(s % 60).toString().padStart(2, '0')}`;
  };

  const handleContainerClick = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (inputRef.current && !testEnded) inputRef.current.focus();
  }, [testEnded]);

  const reset = useCallback(() => {
    if (timerRef.current) clearInterval(timerRef.current);
    isEndingRef.current = false;
    keystrokeTimestampsRef.current = [];
    setUserInput('');
    setTimeLeft(duration);
    setTestStarted(false);
    setTestEnded(false);
    setStartTime(null);
    setCurrentLineIndex(0);
    setLastCorrectSpaceIndex(-1);
    setHasInputError(false);
    setDisplayWPM(0);
  }, [duration]);

  return {
    userInput,
    timeLeft,
    testStarted,
    testEnded,
    hasInputError,
    isFocused,
    setIsFocused,
    lineTransition,
    textBlockWidth,
    displayWPM,
    currentWord,
    renderVisibleText,
    inputRef,
    containerRef,
    handleKeyDown,
    handleInputChange,
    handleContainerClick,
    formatTime,
    reset,
  };
}
